import SearchOutlined from '@ant-design/icons/SearchOutlined';
import Button from 'antd/lib/button';
import DatePicker from 'antd/lib/date-picker';
import Form from 'antd/lib/form';
import message from 'antd/lib/message';
import Popconfirm from 'antd/lib/popconfirm';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { axiosPostCall } from '../api/axiosCall';
import CommonLayout from '../common/CommonLayout';
import DataTable from '../common/DataTable';
import { getColumnSearchProps } from '../common/DataTableSearchTemplate';
import {
  API_END_POINT,
  ORDER_STATUS,
  SOMETHING_WENT_WRONG,
} from '../constant/constants';
import { AuthContext } from '../context/AuthProvider';
import ViewOrderModel from './Order/ViewOrderModel';
import CreateOrderModal from './Order/CreateOrderModal';

const Order = React.memo(() => {
  const [orderList, setOrderList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isViewModelOpen, setIsViewModelOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [orderData, setOrderData] = useState('');

  const [pageConfig, setPageConfig] = useState({
    page: 1,
    pageSize: 10,
    total: 10,
    pageCount: 1,
  });

  const [editingKey, setEditingKey] = useState('');

  const { token } = useContext(AuthContext);
  const debounceTimeoutRef = useRef(null);

  const [form] = Form.useForm();

  const [filterConfig, setFilterConfig] = useState({
    pageSize: 10,
    pageNumber: 1,
  });

  // Debounced filter update function
  const debouncedUpdateFilter = useCallback((newConfig) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setFilterConfig(newConfig);
    }, 300); // 300ms debounce delay
  }, []);

  const getAllOrder = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.GET_ALL_ORDERS,
        header: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          ...filterConfig,
        },
      });
      if (res.hideErrorFromUser) {
        setOrderList(res.data.getOrders);
        setPageConfig(res.data.pagination);
      } else {
        if (!res.data.getOrders.length) {
          message.error(res.error.message);
        }
        setOrderList(res.data.getOrders);
        setPageConfig(res.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error(SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  }, [token, filterConfig]);

  useEffect(() => {
    getAllOrder();
  }, [getAllOrder]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleViewButtonClick = useCallback((data) => {
    setIsViewModelOpen(true);
    setOrderData(data);
  }, []);

  const handleViewModelCloseClick = useCallback(() => {
    setIsViewModelOpen(false);
  }, []);

  const handleCreateOrderClick = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCreateModalCloseClick = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const getOrderStatusId = useCallback((item) => {
    return {
      Confirmed: 1,
      Processing: 2,
      ReadyToDispatch: 3,
      OutForDelivery: 4,
      Cancelled: 5,
    }[item];
  }, []);

  const handleOrderStatusChange = useCallback(
    (value) => {
      let newConfig = { ...filterConfig };
      if (value) {
        newConfig = {
          ...newConfig,
          orderStatusID: getOrderStatusId(value),
        };
      } else {
        delete newConfig.orderStatusID;
      }
      setFilterConfig(newConfig);
    },
    [filterConfig, getOrderStatusId],
  );

  const save = useCallback(
    async (key, rowData) => {
      try {
        setIsLoading(true);
        const res = await axiosPostCall({
          apiEndPoint: API_END_POINT.UPDATE_ORDER_STATUS,
          header: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            orderId: key.orderId,
            companyId: key.companyId,
            orderStatusId: getOrderStatusId(form.getFieldValue().orderStatus),
          },
        });
        if (res?.hideErrorFromUser) {
          message.success(res.data);
          getAllOrder();
          setEditingKey('');
        } else {
          message.error(res?.error?.message);
        }
      } catch (error) {
        console.error('Error updating order status:', error);
        message.error(SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    },
    [token, getOrderStatusId, form, getAllOrder],
  );

  const handleEdit = useCallback(
    (record) => {
      form.setFieldsValue({
        ...record,
      });
      setEditingKey(record.orderNo);
    },
    [form],
  );

  const cancel = useCallback(() => {
    setEditingKey('');
  }, []);

  const isEditing = useCallback(
    (record) => record.orderNo === editingKey,
    [editingKey],
  );

  const handleOrderDateChange = useCallback(
    (orderDate) => {
      let newConfig = {
        ...filterConfig,
      };
      if (orderDate) {
        newConfig = { ...newConfig, orderDate };
      } else {
        delete newConfig.orderDate;
      }
      setFilterConfig(newConfig);
    },
    [filterConfig],
  );

  const columns = useMemo(
    () => [
      {
        key: 'orderId',
        title: 'Order ID',
        dataIndex: 'orderId',
        editable: false,
        ...getColumnSearchProps('orderId'),
      },
      {
        key: 'orderNo',
        title: 'Order Number',
        dataIndex: 'orderNo',
        editable: false,
        ...getColumnSearchProps('orderNo'),
      },
      {
        key: 'companyName',
        title: 'Company Name',
        dataIndex: 'companyName',
        editable: false,
        ...getColumnSearchProps('companyName'),
      },
      {
        key: 'phoneNumber',
        title: 'Phone Number',
        dataIndex: 'phoneNumber',
        editable: false,
      },
      {
        key: 'orderAmount',
        title: 'Order Amount',
        dataIndex: 'orderAmount',
        editable: false,
      },
      {
        key: 'orderStatus',
        title: 'Order Status',
        dataIndex: 'orderStatus',
        editable: true,
        inputType: 'dropdown',
        getSelectOptions: (record) => {
          return ORDER_STATUS;
        },
      },
      {
        key: 'orderDate',
        title: 'Order Date',
        dataIndex: 'orderDate',
        editable: false,
        render: (record) => {
          return moment.utc(record).format('DD/MM/YYYY HH:mm A');
        },
        filterDropdown: () => {
          let orderDate = null;
          return (
            <div className='p-2 flex flex-col space-y-2 justify-center, items-center'>
              <Space>
                <DatePicker
                  onChange={(e) => {
                    orderDate = e ? e.toISOString() : null;
                  }}
                  allowClear={true}
                />
              </Space>
              <Space>
                <Button
                  type='primary'
                  onClick={() => handleOrderDateChange(orderDate)}
                  icon={<SearchOutlined />}
                  size='small'
                  style={{ width: 90 }}
                >
                  Search
                </Button>
                <Button
                  onClick={() => handleOrderDateChange(null)}
                  size='small'
                  style={{ width: 90 }}
                >
                  Reset
                </Button>
              </Space>
            </div>
          );
        },
        filterIcon: (filtered) => (
          <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) => {
          return (
            moment(record[dataIndex]).format('DD-MM-YYYY') ===
            value.format('DD-MM-YYYY')
          );
        },
      },
      {
        title: 'Other',
        editable: false,
        render: (e, record) => {
          const editable = isEditing(record);
          return (
            <div className='flex flex-col space-y-3 '>
              <Button
                onClick={() => {
                  handleViewButtonClick(record);
                }}
              >
                View
              </Button>
              {editable ? (
                <div className='flex flex-row'>
                  <Popconfirm
                    title='Are you Sure to save'
                    onConfirm={() => save(record)}
                    okType='default'
                  >
                    <Button>Save</Button>
                  </Popconfirm>
                  <Button
                    onClick={() => cancel()}
                    style={{
                      marginLeft: 8,
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button onClick={() => handleEdit(record)}>Edit</Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      handleViewButtonClick,
      handleEdit,
      cancel,
      isEditing,
      save,
      handleOrderDateChange,
    ],
  );

  const handleChange = useCallback(
    ({ pagination, filters }) => {
      let newConfig = {
        ...filterConfig,
        pageNumber: pagination.current,
      };

      if (pagination.pageSize !== filterConfig.pageSize) {
        newConfig = {
          ...newConfig,
          pageNumber: 1,
          pageSize: pagination.pageSize,
        };
      }

      // Optimize filter handling
      const filterMappings = {
        orderNo: 'orderNo',
        companyName: 'companyName',
        gstNumber: 'gstNo',
      };

      Object.entries(filterMappings).forEach(([filterKey, configKey]) => {
        if (filters[filterKey]) {
          newConfig[configKey] = filters[filterKey][0];
        } else {
          delete newConfig[configKey];
        }
      });

      // Check if filters changed (for debouncing)
      const hasFilterChanges = Object.keys(filterMappings).some((filterKey) => {
        const currentValue = filterConfig[filterMappings[filterKey]];
        const newValue = filters[filterKey] ? filters[filterKey][0] : undefined;
        return currentValue !== newValue;
      });

      // Use debounced update for search filters, immediate update for pagination
      if (hasFilterChanges) {
        newConfig.pageNumber = 1; // Reset to first page when filtering
        debouncedUpdateFilter(newConfig);
      } else {
        setFilterConfig(newConfig);
      }
    },
    [filterConfig, debouncedUpdateFilter],
  );

  const mergedColumns = useMemo(
    () =>
      columns.map((col) => {
        if (!col.editable) {
          return col;
        }
        return {
          ...col,
          onCell: (record) => {
            return {
              record,
              inputType: col.inputType,
              dataIndex: col.dataIndex,
              title: col.title,
              editing: isEditing(record),
              selectOptions:
                col.inputType === 'dropdown'
                  ? () => col.getSelectOptions(record)
                  : () => {},
            };
          },
        };
      }),
    [columns, isEditing],
  );

  // Memoize DataTable props to prevent unnecessary re-renders
  const dataTableProps = useMemo(
    () => ({
      title: null,
      isTableRowEditable: true,
      data: orderList,
      columns: mergedColumns,
      isSpinning: isLoading,
      rowKey: 'orderId',
      handleChange,
      total: pageConfig.total,
      currentPage: filterConfig.pageNumber,
    }),
    [
      orderList,
      mergedColumns,
      isLoading,
      handleChange,
      pageConfig.total,
      filterConfig.pageNumber,
    ],
  );

  // Memoize Select props
  const selectProps = useMemo(
    () => ({
      style: { width: '50%', paddingTop: '5px' },
      size: 'large',
      allowClear: true,
      placeholder: 'Select order Status',
      onChange: handleOrderStatusChange,
      onClear: () => handleOrderStatusChange(),
      options: ORDER_STATUS,
    }),
    [handleOrderStatusChange],
  );

  return (
    <CommonLayout>
      <div className='flex flex-col space-y-4'>
        <div className='flex justify-between items-center'>
          <Button
            type='primary'
            onClick={handleCreateOrderClick}
            size='large'
            className='border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
          >
            Create Order
          </Button>
          <Select {...selectProps} />
        </div>
        <Form
          form={form}
          component={false}
          validateTrigger={['onBlur']}
        >
          <DataTable {...dataTableProps} />
        </Form>
        <ViewOrderModel
          isOpen={isViewModelOpen}
          order={orderData}
          handleViewModelCloseClick={handleViewModelCloseClick}
        />
        <CreateOrderModal
          isOpen={isCreateModalOpen}
          onClose={handleCreateModalCloseClick}
          onOrderCreated={getAllOrder}
        />
      </div>
    </CommonLayout>
  );
});

export default Order;
